import React, { useState } from 'react';
import { Plus, Calendar, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Input } from '@/shared/components/ui/Input';
import { SearchableSelect, type SearchableOption } from '@/shared/components/ui/SearchableSelect';
import { describeVisitError, useVisitMutations } from '../hooks/useVisits';
import { GpsLocationPickerMap } from './GpsLocationPickerMap';

interface PlanVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultDate: string;
  contactOptions: SearchableOption[];
  assigneeOptions: SearchableOption[];
  purposeOptions: SearchableOption[];
  onVisitCreated?: (visitId: string) => void;
}

export function PlanVisitModal({
  isOpen,
  onClose,
  defaultDate,
  contactOptions,
  assigneeOptions,
  purposeOptions,
  onVisitCreated,
}: PlanVisitModalProps) {
  const { createVisit } = useVisitMutations();

  const [form, setForm] = useState({
    contactId: '',
    assignedTo: '',
    purposeId: '',
    plannedAt: `${defaultDate}T10:00`,
    location: '',
    requirements: '',
    geofenceToleranceMeters: '250',
    lat: '',
    lng: '',
  });

  if (!isOpen) return null;

  async function submitPlan(event: React.FormEvent) {
    event.preventDefault();
    try {
      const coordinates = form.lat && form.lng ? { lat: Number(form.lat), lng: Number(form.lng) } : null;
      const created = await createVisit.mutateAsync({
        contactId: form.contactId,
        assignedTo: form.assignedTo || undefined,
        purposeId: form.purposeId || undefined,
        plannedAt: new Date(form.plannedAt).toISOString(),
        location: form.location,
        requirements: form.requirements,
        geofenceToleranceMeters: Number(form.geofenceToleranceMeters) || 250,
        coordinates,
      });
      toast.success('Visit scheduled successfully.');
      if (onVisitCreated) onVisitCreated(created.visit.id);
      onClose();
      setForm({
        contactId: '',
        assignedTo: '',
        purposeId: '',
        plannedAt: `${defaultDate}T10:00`,
        location: '',
        requirements: '',
        geofenceToleranceMeters: '250',
        lat: '',
        lng: '',
      });
    } catch (error) {
      toast.error(describeVisitError(error, 'Could not plan the visit.'));
    }
  }

  const canSubmit = Boolean(form.contactId && form.plannedAt && form.location.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-150 overflow-hidden">
        {/* Sticky Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white shadow-sm ">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Schedule Field Visit</h2>
              <p className="text-xs text-slate-500">Add a customer stop with location, assigned rep & geofence rules</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={submitPlan} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="grid gap-3.5 sm:grid-cols-2">
              <SearchableSelect
                label="Contact / Customer"
                options={contactOptions}
                value={form.contactId}
                onChange={(value) => {
                  const opt = contactOptions.find((c) => c.value === value);
                  setForm((curr) => ({
                    ...curr,
                    contactId: value,
                    location: curr.location || opt?.description?.split('·')[0]?.trim() || '',
                  }));
                }}
                placeholder="Select customer or contact"
              />
              <SearchableSelect
                label="Assigned Representative"
                options={assigneeOptions}
                value={form.assignedTo}
                onChange={(value) => setForm((curr) => ({ ...curr, assignedTo: value }))}
                placeholder="Select field rep"
              />
              <SearchableSelect
                label="Visit Purpose"
                options={purposeOptions}
                value={form.purposeId}
                onChange={(value) => setForm((curr) => ({ ...curr, purposeId: value }))}
                placeholder="Select purpose"
              />
              <Input
                label="Planned Date & Time"
                type="datetime-local"
                value={form.plannedAt}
                onChange={(e) => setForm((curr) => ({ ...curr, plannedAt: e.target.value }))}
              />
            </div>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <Input
                label="Visit Location / Address"
                value={form.location}
                onChange={(e) => setForm((curr) => ({ ...curr, location: e.target.value }))}
                placeholder="e.g. Plot 42, GIDC Industrial Estate"
              />
              <Input
                label="Geofence Tolerance (Meters)"
                type="number"
                min="0"
                value={form.geofenceToleranceMeters}
                onChange={(e) => setForm((curr) => ({ ...curr, geofenceToleranceMeters: e.target.value }))}
                placeholder="250"
              />
            </div>

            {/* Interactive GPS Map & Place Search Picker */}
            <GpsLocationPickerMap
              lat={form.lat}
              lng={form.lng}
              locationName={form.location}
              onChange={({ lat, lng, address }) => {
                setForm((curr) => ({
                  ...curr,
                  lat,
                  lng,
                  ...(address ? { location: address } : {}),
                }));
              }}
            />

            {/* Requirements / Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Requirements, Target Discussion & Notes
              </label>
              <textarea
                className="min-h-24 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                value={form.requirements}
                onChange={(e) => setForm((curr) => ({ ...curr, requirements: e.target.value }))}
                placeholder="Enter meeting agenda, customer pain points, or demo kit required..."
              />
            </div>
          </div>

          {/* Sticky Modal Actions Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors shadow-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || createVisit.isPending}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2 text-sm font-bold text-white shadow-md  hover:bg-primary-dim disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Plus className="h-4 w-4" />
              {createVisit.isPending ? 'Scheduling…' : 'Schedule Visit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
